import { useCallback, useEffect, useMemo, useState } from 'react';

import { BucketIcon } from '@hugeicons/core-free-icons';
import { Delete02Icon } from '@hugeicons/core-free-icons';
import { UserLove01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { NonCustomerAccountTypes } from 'constants/account-types';
import { AdminPermissions } from 'constants/admin-permissions';
import { countries } from 'constants/countries';
import { AU_STATES, US_STATES } from 'constants/country-admin-divisions';
import {
  IDV_OVERALL_STATUS,
  IDV_OVERALL_STATUS_DISPLAY,
  IDV_SUB_STATUS,
  IDV_SUB_STATUS_DISPLAY,
  IDV_UPDATABLE_STATUS_FIELDS,
  NAME_CHANGE_DOCUMENTATION_ID_TYPES,
} from 'constants/idv';
import { parseIdKey } from 'helpers/ids';
import { setAdminRoute } from 'helpers/session';
import { useAccessControl } from 'hooks/access-control';
import { useClipboard } from 'hooks/clipboard';
import { useDocumentTitle } from 'hooks/document-title';
import { useIdVerificationSummary } from 'hooks/id-verification-summary';
import { useLocale } from 'hooks/locale';
import { useToast } from 'hooks/toast';
import { useApiUploadActions } from 'hooks/upload-actions';
import { useVerifyIdDocument } from 'hooks/verify-id-document';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { OverlayTrigger, Tab, Tabs, Tooltip } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { AddNoteModal } from './components/AddNoteModal/AddNoteModal';
import { AddNotificationModal } from './components/AddNotificationModal/AddNotificationModal';
import { CandidateCheckDetails } from './components/CandidateCheckDetails/CandidateCheckDetails';
import { CandidateCheckValues } from './components/CandidateCheckValues/CandidateCheckValues';
import { CandidateDocumentUploadModal } from './components/CandidateDocumentUpload/CandidateDocumentUploadModal';
import { CandidateUpdateModal } from './components/CandidateUpdateModal/CandidateUpdateModal';
import { IdVerificationModal } from './components/IdVerificationModal/IdVerificationModal';
import { PortalOtpModal } from './components/PortalOtpModal/PortalOtpModal';
import { RejectModal } from './components/RejectModal/RejectModal';
import { SupportSelfieUploadModal } from './components/SupportSelfieUploadModal/SupportSelfieUploadModal';
import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import Alert from 'components/Alert/Alert';
import { CollapsibleBlock } from 'components/CollapsibleBlock/CollapsibleBlock';
import { FilePreview } from 'components/FilePreview/FilePreview';
import { Button, DatePicker, Input } from 'components/FormComponents';
import { InputGroup } from 'components/FormComponents/InputGroup/InputGroup';
import { Select } from 'components/FormComponents/Select/Select';
import { ToggleSwitch } from 'components/FormComponents/ToggleSwitch/ToggleSwitch';
import { IconButton } from 'components/IconButton/IconButton';
import { IconStatusButton } from 'components/IconStatusButton/IconStatusButton';
import { KeyValueModal } from 'components/KeyValueModal/KeyValueModal';
import StatusPill from 'components/Pills/StatusPills';
import { RowDetails } from 'components/RowDetails/RowDetails';
import { CustomToast } from 'components/Toast/CustomToast';

import { loginAs } from 'api/authentication-slice';
import { selectAuthentication } from 'api/authentication-slice';
import { useLazyGetBrandQuery } from 'api/brands-api';
import {
  useRejectCandidateChecksValuesMutation,
  useUpdateCandidateChecksValuesMutation,
} from 'api/candidate-checks-values-api';
import {
  useAddCandidateNoteMutation,
  useAddCandidateNotificationMutation,
  useCancelCandidateChecksMutation,
  useGetAdminCandidateChecksQuery,
  useGetCandidateChecksValuesQuery,
  useGetCandidateCompanyQuery,
  useGetCandidateHistoryQuery,
  useGetCandidateQuery,
  useGetCandidateRefereesQuery,
  useGetCandidateUploadsQuery,
  useLazyGetCandidateHistoryQuery,
  useRequestReConsentMutation,
  useUpdateCandidateMutation,
} from 'api/candidates-api';
import { useGeneratePortalOtpMutation, useLazyGetPortalOtpQuery } from 'api/candidates-api';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import { useGetCompanyChecksQuery } from 'api/company-api';
import { useUpdateIdUploadDetailMutation } from 'api/id-upload-details-api';
import { useGetActiveSupportAccessRequestsQuery } from 'api/support-access-api';

import styles from './AdminCandidate.module.scss';
import { AdminCandidateHistoryTab } from './tabs/AdminCandidateHistoryTab';

const EditIdDetailsForm = ({ idUploadDetail, onUpdateIdDetails, t }) => {
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, watch, setValue, control, reset } = useForm({
    defaultValues: {
      id_holder_name: idUploadDetail?.id_holder_name ?? '',
      id_number: idUploadDetail?.id_number ?? '',
      id_country: idUploadDetail?.id_country ?? idUploadDetail?.selected_id_country ?? '',
      id_state: idUploadDetail?.id_state ?? '',
      id_expiry: idUploadDetail?.id_expiry ?? '',
    },
  });

  // Keep form in sync with id_upload_detail when it changes (e.g. after save + refetch)
  useEffect(() => {
    if (!idUploadDetail) return;
    reset({
      id_holder_name: idUploadDetail.id_holder_name ?? '',
      id_number: idUploadDetail.id_number ?? '',
      id_country: idUploadDetail.id_country ?? idUploadDetail.selected_id_country ?? '',
      id_state: idUploadDetail.id_state ?? '',
      id_expiry: idUploadDetail.id_expiry ?? '',
    });
  }, [
    idUploadDetail?.id_holder_name,
    idUploadDetail?.id_number,
    idUploadDetail?.id_country,
    idUploadDetail?.selected_id_country,
    idUploadDetail?.id_state,
    idUploadDetail?.id_expiry,
    reset,
  ]);

  const idCountry = watch('id_country');
  const idState = watch('id_state');
  const showAuState = idCountry === 'AU';
  const showUsState = idCountry === 'US';
  const stateOptions = showAuState ? AU_STATES : showUsState ? US_STATES : [];

  const onStateChange = e => {
    const value = e?.target?.value ?? '';
    setValue('id_state', value, { shouldDirty: true });
  };

  const onSubmit = async data => {
    if (!onUpdateIdDetails) return;
    const payload = {
      id_holder_name: data.id_holder_name || null,
      id_number: data.id_number || null,
      id_country: data.id_country || null,
      id_state: showAuState || showUsState ? data.id_state || null : null,
      id_expiry: data.id_expiry || null,
    };
    const detailId = idUploadDetail?.id;
    if (detailId == null) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title={t('admin.candidate.idDetailsUpdateFailed', 'Failed to update ID details')}
          type="error"
        />
      ));
      return;
    }
    setSaving(true);
    try {
      await onUpdateIdDetails(detailId, payload);
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title={t('admin.candidate.idDetailsUpdated', 'ID details updated')}
          type="success"
        />
      ));
    } catch (e) {
      toast.custom(toastInstance => (
        <CustomToast
          t={toastInstance}
          title={
            e?.message || t('admin.candidate.idDetailsUpdateFailed', 'Failed to update ID details')
          }
          type="error"
        />
      ));
    } finally {
      setSaving(false);
    }
  };

  const stateLabel = showAuState
    ? t('admin.candidate.idStateTerritory', 'State / Territory')
    : t('admin.candidate.idState', 'State');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column u-gap-small">
      <InputGroup title={t('admin.candidate.idHolderName', 'ID holder name')}>
        <Input register={register} name="id_holder_name" />
      </InputGroup>
      <InputGroup title={t('admin.candidate.idNumber', 'ID number')}>
        <Input register={register} name="id_number" />
      </InputGroup>
      <InputGroup title={t('admin.candidate.idCountry', 'Country')}>
        <Controller
          control={control}
          name="id_country"
          render={({ field: { value, onChange } }) => (
            <Select
              value={value ?? ''}
              useSearch={true}
              useDefault={false}
              placeholder={t('common.selectCountryPlaceholder', 'Select country')}
              inputProps={{
                onChange: e => {
                  const v = e?.target?.value ?? '';
                  onChange(v);
                  setValue('id_state', '');
                },
              }}>
              {(countries || []).map(({ alpha2, name }) => (
                <Select.Item key={alpha2} value={alpha2}>
                  {name}
                </Select.Item>
              ))}
            </Select>
          )}
        />
      </InputGroup>
      {(showAuState || showUsState) && (
        <InputGroup title={stateLabel}>
          <Select
            name="id_state"
            value={idState}
            useSearch={true}
            useDefault={false}
            placeholder={t('common.selectPlaceholder', 'Select')}
            register={register}
            setValue={setValue}
            inputProps={{
              onChange: e => onStateChange(e),
            }}>
            <Select.Item value="">—</Select.Item>
            {(stateOptions || []).map(s => (
              <Select.Item key={s.value} value={s.value}>
                {s.name}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>
      )}
      <InputGroup title={t('admin.candidate.idExpiry', 'ID expiry')}>
        <Controller
          control={control}
          name="id_expiry"
          render={({ field: { onChange, value } }) => (
            <DatePicker
              value={value || ''}
              onChange={onChange}
              outputFormat="DD/MM/YYYY"
              format="dd/MM/yyyy"
              useDefaultToday={false}
            />
          )}
        />
      </InputGroup>
      <Button type="primary" submit disabled={saving}>
        {saving
          ? t('common.saving', 'Saving...')
          : t('admin.candidate.saveIdDetails', 'Save ID details')}
      </Button>
    </form>
  );
};

const IdVerificationForm = ({
  upload,
  linkToNameChangeDocument = false,
  nameChangeDocumentUploads = [],
  idUploadDetail,
  getStatusBoolean,
  onIdStatusUpdate,
  onUpdateIdDetails,
  onSupportRejectId,
  onReinstateIdFromSupport,
  sectionId,
  sectionProps,
  onDelete,
  uploadsMap,
  showAttachToReportToggle = false,
  onToggleAttachToReport,
  t,
}) => {
  const backUpload =
    sectionId === 'candidateIds' && idUploadDetail?.back_upload_id && uploadsMap
      ? uploadsMap.get(idUploadDetail.back_upload_id)
      : null;
  const hasDual = !!backUpload;
  const displaySlots =
    sectionId === 'candidateIds' && idUploadDetail && (upload || backUpload)
      ? hasDual
        ? [
            {
              label: t('admin.candidate.idVerification.front', 'Front'),
              file: upload,
            },
            {
              label: t('admin.candidate.idVerification.back', 'Back'),
              file: backUpload,
            },
          ].filter(slot => slot.file)
        : [{ label: '', file: upload }].filter(slot => slot.file)
      : [];
  const showIdDocumentFiles = displaySlots.length > 0;

  const isRejectedSection = !!sectionProps?.rejected;
  const showIdDetailPanel =
    idUploadDetail &&
    upload?.metadata?.id_type &&
    (sectionId === 'candidateIds' || isRejectedSection);
  const allowVerificationToggles =
    sectionId === 'candidateIds' && idUploadDetail?.status !== IDV_OVERALL_STATUS.REJECTED;
  const showEditIdDetailsForm =
    onUpdateIdDetails &&
    !(isRejectedSection && idUploadDetail?.status === IDV_OVERALL_STATUS.REJECTED);

  // Filter out fields that are 'not_applicable'
  const editableStatusFields = IDV_UPDATABLE_STATUS_FIELDS.filter(
    fieldName => idUploadDetail?.[fieldName] !== IDV_SUB_STATUS.NOT_APPLICABLE,
  );

  // Build default values only for editable fields
  const defaultValues = {};
  editableStatusFields.forEach(fieldName => {
    defaultValues[fieldName] = getStatusBoolean(idUploadDetail?.[fieldName]);
  });
  // Include name_linkage_upload_id if linkToNameChangeDocument is enabled
  if (linkToNameChangeDocument) {
    defaultValues.name_linkage_upload_id = idUploadDetail?.name_linkage_upload_id || null;
  }

  const { register, handleSubmit, watch, setValue, reset, control } = useForm({
    defaultValues,
  });
  const nameLinkageUploadId = watch('name_linkage_upload_id');

  useEffect(() => {
    reset(defaultValues);
  }, [
    idUploadDetail?.id,
    idUploadDetail?.authentication_status,
    idUploadDetail?.id_type_match_status,
    idUploadDetail?.country_match_status,
    idUploadDetail?.expiry_tolerance_status,
    idUploadDetail?.name_match_status,
    idUploadDetail?.name_linkage_upload_id,
    reset,
  ]);

  const onSubmit = data => {
    onIdStatusUpdate(upload.id, idUploadDetail?.id, data, idUploadDetail);
  };

  // Get status display helper for overall status
  const getOverallStatusDisplay = status => {
    if (!status) return null;
    return IDV_OVERALL_STATUS_DISPLAY[status] || { title: status, color: 'neutral' };
  };

  // Get status display helper for sub-statuses
  const getSubStatusDisplay = status => {
    if (!status) return null;
    return IDV_SUB_STATUS_DISPLAY[status] || { title: status, color: 'neutral' };
  };

  const overallStatus = idUploadDetail?.status;
  const faceSimilarityStatus = idUploadDetail?.face_similarity_status;
  const overallStatusDisplay = getOverallStatusDisplay(overallStatus);
  const faceSimilarityDisplay = getSubStatusDisplay(faceSimilarityStatus);
  const faceSimilarityScore = idUploadDetail?.face_similarity;
  const faceSimilarityPercent =
    faceSimilarityScore != null && Number.isFinite(Number(faceSimilarityScore))
      ? Number(faceSimilarityScore).toFixed(2)
      : null;
  const faceSimilarityPillText = faceSimilarityDisplay
    ? faceSimilarityPercent
      ? `${faceSimilarityDisplay.title} (${faceSimilarityPercent}%)`
      : faceSimilarityDisplay.title
    : null;

  const filePreviewRejected = !!sectionProps?.rejected && !showIdDetailPanel;

  const showNotNeededButton =
    sectionId === 'candidateIds' &&
    idUploadDetail &&
    onSupportRejectId &&
    idUploadDetail.status !== IDV_OVERALL_STATUS.REJECTED;

  return (
    <div className="d-flex flex-column u-gap u-border u-padding u-border-radius min-w-0">
      {showIdDocumentFiles ? (
        <div className="d-flex flex-wrap gap-default align-items-start">
          {displaySlots.map((slot, index) => (
            <div key={index} className="d-flex flex-column u-gap-small min-w-0">
              {slot.label ? (
                <span className="u-font-weight-600 u-font-size-small">{slot.label}</span>
              ) : null}
              <FilePreview
                file={slot.file}
                {...sectionProps}
                rejected={filePreviewRejected}
                onDelete={sectionProps.allowDelete ? onDelete : undefined}
              />
            </div>
          ))}
        </div>
      ) : (
        <>
          <FilePreview
            file={upload}
            {...sectionProps}
            rejected={filePreviewRejected}
            onDelete={onDelete}
          />
          {showAttachToReportToggle && onToggleAttachToReport && (
            <div className="d-flex align-items-center u-gap-small u-margin-top--small">
              <ToggleSwitch
                label={t('admin.candidate.supportingDocumentsAttachToReport', 'Attach to report')}
                inputProps={{
                  checked: upload?.metadata?.attach_to_report !== false,
                  onChange: e => onToggleAttachToReport(upload.id, e.target.checked),
                }}
                status={
                  upload?.metadata?.attach_to_report === false
                    ? {
                        text: t(
                          'admin.candidate.supportingDocumentsDoNotAttachToReport',
                          'Do not attach to report',
                        ),
                        status: 'info',
                      }
                    : {
                        text: t(
                          'admin.candidate.supportingDocumentsAttachToReport',
                          'Attach to report',
                        ),
                        status: 'success',
                      }
                }
              />
            </div>
          )}
        </>
      )}
      {showIdDetailPanel && (
        <div className="d-flex flex-column u-gap min-w-0">
          {showEditIdDetailsForm && (
            <CollapsibleBlock>
              <CollapsibleBlock.Header>
                <span className="text-nowrap">
                  {t(
                    'admin.candidate.editIdDetails',
                    "Edit ID Details - only use to add provider required info if missing (e.g. you've received a bot message).",
                  )}
                </span>
              </CollapsibleBlock.Header>
              <CollapsibleBlock.Content>
                <div className="min-w-0">
                  <EditIdDetailsForm
                    idUploadDetail={idUploadDetail}
                    onUpdateIdDetails={onUpdateIdDetails}
                    t={t}
                  />
                </div>
              </CollapsibleBlock.Content>
            </CollapsibleBlock>
          )}
          {overallStatusDisplay && !isRejectedSection && (
            <div className="d-flex align-items-center u-gap-small">
              <span className="u-font-weight-600">Overall Status:</span>
              <StatusPill text={overallStatusDisplay.title} status={overallStatusDisplay.color} />
            </div>
          )}
          {isRejectedSection &&
            idUploadDetail &&
            onReinstateIdFromSupport &&
            idUploadDetail.status === IDV_OVERALL_STATUS.REJECTED && (
              <div>
                <Button type="primary" onClick={() => onReinstateIdFromSupport(idUploadDetail.id)}>
                  {t('admin.candidate.reinstateId')}
                </Button>
              </div>
            )}
          {faceSimilarityStatus &&
            faceSimilarityStatus !== IDV_SUB_STATUS.NOT_APPLICABLE &&
            !isRejectedSection && (
              <div className="d-flex align-items-center u-gap-small">
                <span className="u-font-weight-600">
                  {t('admin.candidate.idVerification.face_similarity_status')}:
                </span>
                <StatusPill text={faceSimilarityPillText} status={faceSimilarityDisplay.color} />
              </div>
            )}
          {allowVerificationToggles && editableStatusFields.length > 0 && (
            <form onSubmit={handleSubmit(onSubmit)}>
              {editableStatusFields.map(fieldName => {
                const status = idUploadDetail?.[fieldName];
                const label = t(`admin.candidate.idVerification.${fieldName}`) + ':';
                const statusDisplay = getSubStatusDisplay(status);
                return (
                  <Controller
                    key={fieldName}
                    name={fieldName}
                    control={control}
                    render={({ field }) => (
                      <ToggleSwitch
                        name={fieldName}
                        label={label}
                        inputProps={{
                          checked: !!field.value,
                          onChange: e => field.onChange(e.target.checked),
                          onBlur: field.onBlur,
                          ref: field.ref,
                        }}
                        status={
                          statusDisplay
                            ? { text: statusDisplay.title, status: statusDisplay.color }
                            : null
                        }
                      />
                    )}
                  />
                );
              })}
              {linkToNameChangeDocument && (
                <InputGroup title="Link to Name Change Document">
                  <Select
                    name="name_linkage_upload_id"
                    placeholder="Select your name change document"
                    register={register}
                    setValue={setValue}
                    value={nameLinkageUploadId}>
                    {(nameChangeDocumentUploads || []).map(option => {
                      const idType =
                        option?.id_upload_detail?.selected_id_type || option?.metadata?.id_type;
                      const idTypeName = t(`${idType}.name`, {
                        ns: 'id_document_types',
                        returnObjects: true,
                      });
                      return (
                        <Select.Item key={option.id} value={option.id}>
                          {idTypeName} ({option?.file_name})
                        </Select.Item>
                      );
                    })}
                  </Select>
                </InputGroup>
              )}
              <div className="d-flex flex-wrap gap-default align-items-center u-margin-top--small">
                <Button submit={true}>Update</Button>
                {showNotNeededButton && (
                  <Button type="primary" onClick={() => onSupportRejectId(idUploadDetail.id)}>
                    {t('admin.candidate.supportRejectId')}
                  </Button>
                )}
              </div>
            </form>
          )}
          {allowVerificationToggles && editableStatusFields.length === 0 && showNotNeededButton && (
            <div className="d-flex flex-wrap gap-default align-items-center">
              <Button type="primary" onClick={() => onSupportRejectId(idUploadDetail.id)}>
                {t('admin.candidate.supportRejectId')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const isUploadInAdminRejectedSection = (upload, rejectedFileIdSet) => {
  if (!upload) return false;
  if (rejectedFileIdSet.has(String(upload.id))) return true;
  if (
    upload.metadata?.id_type &&
    upload.id_upload_detail &&
    upload.id_upload_detail.status === IDV_OVERALL_STATUS.REJECTED
  ) {
    return true;
  }
  return false;
};

const AdminCandidate = () => {
  let { candidate_id: candidateId } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation(['translation', 'id_document_types']);
  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication.admin_permissions);
  const copyToClipboard = useClipboard();
  const showToast = useToast();

  const handleCopyToClipboard = (text, message) => {
    if (!text) return;
    copyToClipboard(text);
    showToast('Copied', message, 'info');
  };

  const { data: company, refetch: refetchCompany } = useGetCandidateCompanyQuery(candidateId, {
    refetchOnMountOrArgChange: true,
  });

  const { data: activeRequests } = useGetActiveSupportAccessRequestsQuery(undefined, {
    skip: company?.support_access_level !== 'request',
  });
  const hasActiveRequestForCompany = (activeRequests || []).some(r => r.company_id === company?.id);
  const { data: companyChecksData, refetch: refetchCompanyChecks } = useGetCompanyChecksQuery(
    company?.id,
    {
      skip: !company?.id,
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: candidateData, refetch: refetchCandidate } = useGetCandidateQuery(candidateId, {
    refetchOnMountOrArgChange: true,
  });
  const {
    data: checksData,
    isLoading: loadingChecks,
    refetch: refetchChecks,
  } = useGetAdminCandidateChecksQuery(candidateId);
  const { data: checkTypes } = useGetCheckTypesQuery();
  const { data: checkValuesData } = useGetCandidateChecksValuesQuery(candidateId);
  const { data: uploadsData, refetch: refetchUploads } = useGetCandidateUploadsQuery({
    id: candidateId,
    params: { with_metadata: true },
  });

  const {
    isLoading: isIdvLoading,
    idvStatus,
    idvStatusKey,
    faceSimilarity,
    selfie,
    idDoc,
    idDocInfo,
    refetch: refetchIdv,
  } = useIdVerificationSummary(candidateId);
  const idvSummary = { idvStatus, idvStatusKey, faceSimilarity, selfie, idDoc, idDocInfo };

  const { data: refereesData } = useGetCandidateRefereesQuery(candidateId);
  const { data: checkNotesData, refetch: refetchCheckNotes } = useGetCandidateHistoryQuery({
    id: candidateId,
    params: { internal: true },
  });

  const [getCandidateHistory] = useLazyGetCandidateHistoryQuery();
  const [rejectCandidateChecksValues] = useRejectCandidateChecksValuesMutation();
  const [updateCandidateChecksValues] = useUpdateCandidateChecksValuesMutation();
  const [updateCandidate] = useUpdateCandidateMutation();
  const [cancelCandidateChecks] = useCancelCandidateChecksMutation();
  const [requestReConsent] = useRequestReConsentMutation();
  const [addCandidateNote] = useAddCandidateNoteMutation();
  const [addCandidateNotification] = useAddCandidateNotificationMutation();
  const [updateIdUploadDetail] = useUpdateIdUploadDetailMutation();
  const [generatePortalOtp] = useGeneratePortalOtpMutation();
  const [getPortalOtp] = useLazyGetPortalOtpQuery();
  const { verify: verifyIdDocument, isVerifying: isVerifyingId } = useVerifyIdDocument(
    { candidateId },
    {
      onSuccess: async () => {
        await Promise.all([refetchIdv(), refetchUploads()]);
      },
    },
  );

  const [loading, setLoading] = useState(false);

  const [cancelConfirmation, setCancelConfirmation] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [restoreConfirmation, setRestoreConfirmation] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [addNoteModal, setAddNoteModal] = useState(false);
  const [addNotificationModal, setAddNotificationModal] = useState(false);
  const [updateCandidateModal, setUpdateCandidateModal] = useState(false);
  const [showIdVerificationModal, setShowIdVerificationModal] = useState(false);
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState(false);
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [showSupportSelfieUploadModal, setShowSupportSelfieUploadModal] = useState(false);
  const [showPortalOtpModal, setShowPortalOtpModal] = useState(false);
  const [showReConsentConfirmation, setShowReConsentConfirmation] = useState(false);

  const [candidate, setCandidate] = useState();
  const [checks, setChecks] = useState([]);
  const [companyChecks, setCompanyChecks] = useState([]);
  const [referees, setReferees] = useState([]);
  const [rejectedUploads, setRejectedUploads] = useState([]);
  const [checkValues, setCheckValues] = useState([]);
  const [idUploads, setIdUploads] = useState([]);
  const [selfieUpload, setSelfieUpload] = useState(null);
  const [signatureUploads, setSignatureUploads] = useState([]);
  const [supportUploads, setSupportUploads] = useState([]);
  const [otherDocumentsUploads, setOtherDocumentsUploads] = useState([]);
  const [resultUploads, setResultUploads] = useState([]);
  const [consentsDisclosuresUploads, setConsentsDisclosuresUploads] = useState([]);

  const [historyData, setHistoryData] = useState([]);

  const { dateSlashFormat } = useLocale(company?.locale);

  const { deleteUpload, updateUpload } = useApiUploadActions();

  const [deleteUploadIds, setDeleteUploadIds] = useState([]);
  const [showDeleteUploadAlert, setShowDeleteUploadAlert] = useState(false);
  const [brand, setBrand] = useState(null);
  const [currentBrandId, setCurrentBrandId] = useState(null);

  const [getBrand, { data: brandData }] = useLazyGetBrandQuery();

  useEffect(() => {
    if (candidate?.brand_id) {
      setBrand(null);
      setCurrentBrandId(candidate.brand_id);
      getBrand(candidate.brand_id);
    } else {
      setBrand(null);
      setCurrentBrandId(null);
    }
  }, [candidate?.brand_id, getBrand]);

  useEffect(() => {
    if (currentBrandId !== null && brandData?.id === currentBrandId) {
      setBrand(brandData);
    }
  }, [brandData, currentBrandId]);

  const getCheckNotes = useCallback(
    checkId => {
      return (checkNotesData || []).filter(note => note.candidate_check_id === checkId);
    },
    [checkNotesData],
  );

  const fetchHistoryData = useCallback(async () => {
    if (candidate?.id) {
      const { data } = await getCandidateHistory({ id: candidate.id });
      setHistoryData(data || []);
    }
  }, [candidate?.id, getCandidateHistory, checkNotesData]);

  useDocumentTitle(candidate ? candidate?.name : 'Loading...');

  useEffect(() => {
    if (candidateData) setCandidate(candidateData);
  }, [candidateData]);
  useEffect(() => {
    if (checkValuesData) {
      setCheckValues({
        ...checkValuesData?.check_values,
        admin_field_metadata: checkValuesData?.admin_field_metadata,
      });
    }
  }, [checkValuesData]);
  useEffect(() => {
    if (checksData) setChecks(checksData);
  }, [checksData]);
  const orderedChecks = useMemo(() => {
    return [...(checks || [])]
      .filter(check => !check.dummy)
      .sort((a, b) => {
        const titleA =
          checkTypes?.find(checkType => checkType.slug === a.type)?.title || a.type || '';
        const titleB =
          checkTypes?.find(checkType => checkType.slug === b.type)?.title || b.type || '';
        return titleA.localeCompare(titleB, undefined, { sensitivity: 'base' });
      });
  }, [checks, checkTypes]);
  useEffect(() => {
    if (companyChecksData) setCompanyChecks(companyChecksData);
  }, [companyChecksData]);
  useEffect(() => {
    if (refereesData) setReferees(refereesData);
  }, [refereesData]);
  useEffect(() => {
    if (!uploadsData) return;
    const rejectedFiles = checkValuesData?.check_values?.rejected_values?.files || [];
    const rejectedSet = new Set(rejectedFiles.map(id => String(id)));
    const isRejected = u => isUploadInAdminRejectedSection(u, rejectedSet);

    setRejectedUploads((uploadsData || []).filter(upload => isRejected(upload)));

    setIdUploads(
      (uploadsData || []).filter(upload => upload.metadata?.id_type && !isRejected(upload)),
    );
    setSelfieUpload(
      (uploadsData || [])
        .filter(upload => upload.metadata?.selfie)
        .reduce((latest, upload) => (!latest || upload.id > latest.id ? upload : latest), null),
    );
    setSignatureUploads(
      (uploadsData || []).filter(
        upload => upload.metadata?.signature || upload.metadata?.guardian_signature,
      ),
    );

    setSupportUploads(
      (uploadsData || []).filter(upload => {
        const m = upload.metadata;
        return m?.check_type || m?.bot || m?.field_code;
      }),
    );

    setOtherDocumentsUploads((uploadsData || []).filter(upload => upload.metadata?.dispute_form));

    setConsentsDisclosuresUploads(
      (uploadsData || []).filter(upload => {
        const m = upload.metadata;
        return m?.consents || m?.fcra_disclosures;
      }),
    );
    setResultUploads(
      (uploadsData || []).filter(upload => upload.metadata?.result || upload.metadata?.sub_result),
    );

    refetchChecks();
  }, [uploadsData, checkValuesData?.check_values?.rejected_values?.files]);

  const isNameChangeDocumentUploaded = useMemo(() => {
    return (idUploads || []).filter(upload => {
      const { idType } = parseIdKey(upload?.metadata?.id_type);
      return idType && NAME_CHANGE_DOCUMENTATION_ID_TYPES.includes(idType);
    });
  }, [idUploads]);

  // Map upload id -> upload for resolving preview_upload_ids and back_upload_id
  const uploadsMap = useMemo(() => {
    const map = new Map();
    (uploadsData || []).forEach(u => map.set(u.id, u));
    return map;
  }, [uploadsData]);

  // One group per id_upload_detail (front upload): includes front, optional back, and preview images
  const idDocumentGroups = useMemo(() => {
    if (!uploadsData) return [];
    const rejectedFiles = checkValuesData?.check_values?.rejected_values?.files || [];
    const rejectedSet = new Set(rejectedFiles.map(id => String(id)));
    return (uploadsData || [])
      .filter(
        u =>
          u.metadata?.id_type &&
          u.id_upload_detail &&
          !isUploadInAdminRejectedSection(u, rejectedSet),
      )
      .map(frontUpload => {
        const detail = frontUpload.id_upload_detail;
        const backUpload = detail?.back_upload_id ? uploadsMap.get(detail.back_upload_id) : null;
        const previewUploads = (detail?.preview_upload_ids || [])
          .map(id => uploadsMap.get(id))
          .filter(Boolean);
        return { frontUpload, idUploadDetail: detail, backUpload, previewUploads };
      });
  }, [uploadsData, checkValuesData?.check_values?.rejected_values?.files, uploadsMap]);

  // Cancel
  const cancelCheck = async () => {
    setLoading(true);
    setCancelConfirmation(false);
    const { data: result } = await cancelCandidateChecks(candidateId);
    setCandidate(result);
    setLoading(false);
  };

  // Request re-consent
  const handleRequestReConsent = async () => {
    setLoading(true);
    setShowReConsentConfirmation(false);
    const { data: result } = await requestReConsent(candidateId);
    setCandidate(result);
    setLoading(false);
  };

  // Reject
  const handleReject = async fields => {
    setLoading(true);
    const { data: result } = await rejectCandidateChecksValues({
      candidate_id: candidateId,
      rejected_fields: fields,
    });
    setCheckValues(result);
    setLoading(false);
  };

  // Update
  const handleUpdate = async data => {
    setLoading(true);
    const { data: result } = await updateCandidate({ id: candidateId, data });
    setCandidate(result);
    setLoading(false);
  };

  // Update candidate checks values
  const handleUpdateCandidateChecksValues = async data => {
    setLoading(true);
    const { data: result } = await updateCandidateChecksValues({
      candidate_id: candidateId,
      values: data,
    });
    setCheckValues(result);
    setLoading(false);
  };

  // Login as client user
  const loginAsClient = async () => {
    setLoading(true);
    try {
      await dispatch(loginAs(candidate.user_id)).unwrap();
      setAdminRoute(`/admin/candidates/${candidate.id}`);
      navigate(`/check/${candidate.id}/background_checks`);
    } catch {
      showToast(
        'Error',
        'Could not log in as this user. The company may not allow support access.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  // Update check data
  const addOrUpdateCheck = check => {
    if (!check?.id) return;

    if (checks.findIndex(c => c.id === check.id) >= 0)
      setChecks(curr => curr.map(c => (c.id === check.id ? check : c)));
    else setChecks(curr => [...curr, check]);
    refetchChecks();
    refetchCandidate();
    refetchCompany();
    refetchCompanyChecks();
  };

  // Restore candidate
  const restoreCandidate = () => {
    setRestoreConfirmation(false);
    handleUpdate({ deleted: false });
  };

  // Delete candidate
  const deleteCandidate = () => {
    setDeleteConfirmation(false);
    handleUpdate({ deleted: true });
  };

  // Update candidate details
  const updateCandidateDetails = data => {
    setUpdateCandidateModal(false);
    handleUpdate(data);
  };

  // Add candidate note
  const handleAddCandidateNote = async ({ message }) => {
    setLoading(true);
    setAddNoteModal(false);
    await addCandidateNote({ id: candidateId, message });
    setLoading(false);
  };

  // Add candidate note
  const handleAddCandidateNotification = async params => {
    try {
      setLoading(true);
      await addCandidateNotification({ id: candidateId, params });
      setLoading(false);
      setAddNotificationModal(false);
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Nicely done - Email sent"
            message={`Email sent successfully to ${params.recipient}.`}
            type="success"
          />
        ),
        {
          duration: 5000,
        },
      );
    } catch (error) {
      toast.custom(
        t => <CustomToast t={t} title={`Oops - ${error.message || error}`} type="error" />,
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUpload = async () => {
    const ids = Array.isArray(deleteUploadIds) ? deleteUploadIds : [deleteUploadIds];
    try {
      setLoading(true);
      for (const id of ids) {
        await deleteUpload(id);
      }
      await Promise.all([refetchUploads(), refetchIdv()]);
      setShowDeleteUploadAlert(false);
      setDeleteUploadIds([]);
    } catch (error) {
      const message =
        (typeof error?.data === 'string' && error.data) ||
        error?.data?.message ||
        error?.error ||
        error?.message ||
        t('common.error', 'Error');
      toast.custom(
        toastInstance => <CustomToast t={toastInstance} title={message} type="error" />,
        { duration: 5000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteUpload = idOrIds => {
    setDeleteUploadIds(Array.isArray(idOrIds) ? idOrIds : [idOrIds]);
    setShowDeleteUploadAlert(true);
  };

  // Check if all IDs are verified (or not_applicable) and no _not_fulfilled fields are true
  // If so, auto-set id_requirements_met on all applicable checks
  const checkAndAutoSetIdRequirementsMet = async freshUploads => {
    const rejectedFileIds = new Set(
      (checkValues?.rejected_values?.files || []).map(id => String(id)),
    );
    const isExcludedFromActiveIds = u => isUploadInAdminRejectedSection(u, rejectedFileIds);

    // Get ID uploads that have an id_upload_detail (one per ID document - front uploads only;
    // back-of-license uploads have metadata.id_type but no id_upload_detail, so exclude them)
    const allIdUploads = (freshUploads || []).filter(
      upload =>
        upload.metadata?.id_type &&
        upload.id_upload_detail != null &&
        !isExcludedFromActiveIds(upload),
    );

    const rejectedIdUploads = (freshUploads || []).filter(
      upload => upload.metadata?.id_type && isExcludedFromActiveIds(upload),
    );

    // If there are any rejected ID uploads, requirements are not met
    if (rejectedIdUploads.length > 0) {
      return;
    }

    // If there are no ID uploads at all, we can't determine if requirements are met
    if (allIdUploads.length === 0) {
      return;
    }

    // Check if ALL ID uploads have verified or not_applicable status (matches id-requirements.js isVerifiedUpload)
    const allVerifiedOrNotApplicable = allIdUploads.every(upload => {
      const status = upload.id_upload_detail?.status;
      return status === IDV_OVERALL_STATUS.VERIFIED || status === IDV_OVERALL_STATUS.NOT_APPLICABLE;
    });

    if (!allVerifiedOrNotApplicable) {
      // Only clear id_requirements_met when there is a single ID - we know that ID was toggled
      // and no check can have requirements met. With multiple IDs we can't know which check(s) to clear.
      const checksToClear =
        allIdUploads.length === 1
          ? (checks || []).filter(check => {
              const checkType = (checkTypes || []).find(ct => ct.slug === check.type);
              return checkType?.has_id_requirements && check.id_requirements_met === true;
            })
          : [];
      if (checksToClear.length > 0) {
        try {
          await bulkUpdateCandidateChecks({
            ids: checksToClear.map(c => c.id),
            data: { id_requirements_met: false },
          }).unwrap();
          await refetchChecks();
          toast.custom(
            t => (
              <CustomToast
                t={t}
                title="ID requirements"
                message={`Cleared ID requirements met on ${checksToClear.length} check(s)`}
                type="info"
              />
            ),
            { duration: 3000 },
          );
        } catch (error) {
          console.error('Failed to clear id_requirements_met:', error);
        }
      }
      return;
    }

    // Check if there are any _not_fulfilled fields set to true
    const values = checkValues?.values || {};
    const hasNotFulfilledIssues = Object.entries(values).some(
      ([key, value]) => key.includes('_not_fulfilled') && value === true,
    );

    if (hasNotFulfilledIssues) {
      return;
    }

    // Get check IDs that have ID requirements and don't already have id_requirements_met set
    const checksToUpdate = (checks || []).filter(check => {
      const checkType = (checkTypes || []).find(ct => ct.slug === check.type);
      return checkType?.has_id_requirements && !check.id_requirements_met;
    });

    if (checksToUpdate.length === 0) {
      return;
    }

    // Bulk update all applicable checks
    try {
      await bulkUpdateCandidateChecks({
        ids: checksToUpdate.map(c => c.id),
        data: { id_requirements_met: true },
      }).unwrap();

      await refetchChecks();

      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="ID Requirements Met"
            message={`Auto-set ID requirements met on ${checksToUpdate.length} check(s)`}
            type="success"
          />
        ),
        {
          duration: 3000,
        },
      );
    } catch (error) {
      console.error('Failed to auto-set id_requirements_met:', error);
    }
  };

  const handleUpdateIdDetails = async (idUploadDetailId, data) => {
    await updateIdUploadDetail({ id: idUploadDetailId, data }).unwrap();
    Promise.all([refetchUploads(), refetchIdv()]).catch(() => {});
  };

  const handleSupportRejectId = async idUploadDetailId => {
    try {
      setLoading(true);
      await updateIdUploadDetail({
        id: idUploadDetailId,
        data: { status: IDV_OVERALL_STATUS.REJECTED },
      }).unwrap();
      const [{ data: freshUploads }] = await Promise.all([refetchUploads(), refetchIdv()]);
      await checkAndAutoSetIdRequirementsMet(freshUploads);
      toast.custom(
        toastInstance => (
          <CustomToast
            t={toastInstance}
            title={t('admin.candidate.supportRejectIdSuccess')}
            type="success"
          />
        ),
        { duration: 3000 },
      );
    } catch (error) {
      toast.custom(
        toastInstance => (
          <CustomToast
            t={toastInstance}
            title={error.message || t('common.error', 'Error')}
            type="error"
          />
        ),
        { duration: 5000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReinstateIdFromSupport = async idUploadDetailId => {
    try {
      setLoading(true);
      await updateIdUploadDetail({
        id: idUploadDetailId,
        data: { reinstate_from_support_reject: true },
      }).unwrap();
      const [{ data: freshUploads }] = await Promise.all([refetchUploads(), refetchIdv()]);
      await checkAndAutoSetIdRequirementsMet(freshUploads);
      toast.custom(
        toastInstance => (
          <CustomToast
            t={toastInstance}
            title={t('admin.candidate.reinstateIdSuccess')}
            type="success"
          />
        ),
        { duration: 3000 },
      );
    } catch (error) {
      toast.custom(
        toastInstance => (
          <CustomToast
            t={toastInstance}
            title={error.message || t('common.error', 'Error')}
            type="error"
          />
        ),
        { duration: 5000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateIDVerificationStatus = async (
    uploadId,
    idUploadDetailId,
    data,
    idUploadDetail,
  ) => {
    // Convert boolean values to status strings
    const statusData = {};

    const nameLinkageUploadId = data?.name_linkage_upload_id || null;

    IDV_UPDATABLE_STATUS_FIELDS.forEach(fieldName => {
      if (data[fieldName] !== undefined) {
        statusData[fieldName] = data[fieldName] ? IDV_SUB_STATUS.PASSED : IDV_SUB_STATUS.FAILED;
      }
    });

    try {
      setLoading(true);

      // If photo ID with no face score yet, run face verification first (e.g. selfie was added later)
      if (
        idUploadDetail?.has_photo &&
        idUploadDetail?.face_similarity == null &&
        idUploadDetail?.selected_id_type
      ) {
        await verifyIdDocument({
          uploadId,
          idCode: idUploadDetail.selected_id_code || null,
          idType: idUploadDetail.selected_id_type,
          idCountry: idUploadDetail.selected_id_country || null,
          backUploadId: idUploadDetail.back_upload_id || null,
        });
      }

      const updatedDetail = await updateIdUploadDetail({
        id: idUploadDetailId,
        data: { ...statusData, name_linkage_upload_id: nameLinkageUploadId },
      }).unwrap();

      // Refetch ID verification summary (may depend on updated status)
      // Refetch uploads to get updated id_upload_detail and check id_requirements_met
      const [{ data: freshUploads }] = await Promise.all([refetchUploads(), refetchIdv()]);

      // Ensure the upload we just updated has the latest id_upload_detail when evaluating
      // id_requirements_met (avoids relying on refetch returning updated data immediately).
      // Merge partial API response into existing detail so status etc. are not dropped.
      const uploadsForEval =
        Array.isArray(freshUploads) && updatedDetail
          ? freshUploads.map(u =>
              u.id === uploadId
                ? { ...u, id_upload_detail: { ...u.id_upload_detail, ...updatedDetail } }
                : u,
            )
          : freshUploads;

      // Check if we should auto-set id_requirements_met
      await checkAndAutoSetIdRequirementsMet(uploadsForEval);

      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Success"
            message="ID verification status updated successfully"
            type="success"
          />
        ),
        {
          duration: 3000,
        },
      );
    } catch (error) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Error"
            message={error.message || 'Failed to update ID verification status'}
            type="error"
          />
        ),
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  // Verify ID
  const handleVerifyIdDocument = async ({
    uploadId,
    idCode,
    idType,
    idCountry = null,
    expiryTolerance = null,
    selfieUploadId = null,
    backUploadId = null,
  }) => {
    setLoading(true);
    setShowIdVerificationModal(false);
    try {
      if (!uploadId || !idType) throw new Error('uploadId and idType are required');
      await verifyIdDocument({
        uploadId,
        idType,
        idCountry,
        expiryTolerance,
        selfieUploadId,
        backUploadId,
      });
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleSupportSelfieUploaded = async () => {
    const [{ data: freshUploads }] = await Promise.all([refetchUploads(), refetchIdv()]);
    await checkAndAutoSetIdRequirementsMet(freshUploads);
  };

  const handleAfterUpload = async (
    createdUpload,
    selectedIdType,
    selectedCountryAlpha2,
    backUploadId = null,
  ) => {
    const hasSelfie = !!selfieUpload?.id;

    // Always verify IDs uploaded from admin page to create id_upload_detail records
    // For photo IDs, include selfie if available; for non-photo IDs, verify without selfie
    if (createdUpload?.id && selectedIdType?.value) {
      try {
        await handleVerifyIdDocument({
          uploadId: createdUpload.id,
          idType: selectedIdType.value,
          idCountry: selectedCountryAlpha2 || null,
          expiryTolerance: null,
          selfieUploadId: hasSelfie ? selfieUpload.id : null,
          backUploadId: backUploadId || null,
        });
        // Ensure uploads are refetched after verification completes
        await Promise.all([refetchUploads(), refetchIdv()]);
      } catch (error) {
        console.error('Error verifying ID document:', error);
        // Still refetch uploads even if verification fails, in case the upload was created
        await refetchUploads();
      }
    }
  };

  const actions = useMemo(() => {
    return [
      {
        icon: 'settings',
        title: t('admin.candidate.updateCandidateDetails'),
        onClick: () => setUpdateCandidateModal(true),
      },
      {
        icon: 'briefcase',
        title: t('admin.candidate.companyDetails.title'),
        onClick: () => setShowCompanyDetailsModal(true),
      },
      {
        icon: 'x',
        title: t('common.cancelCandidate'),
        onClick: () => setCancelConfirmation(true),
      },
      {
        icon: 'user-x',
        title: t('admin.candidate.declineBackgroundDetails'),
        onClick: () => setRejectModal(true),
      },
      {
        icon: 'file-text',
        title: candidate?.re_consent_requested_at
          ? t('admin.candidate.reConsentPending')
          : t('admin.candidate.requestReConsent'),
        onClick: () => setShowReConsentConfirmation(true),
        disabled: !!candidate?.re_consent_requested_at,
        hide:
          !candidate?.submitted_at ||
          candidate?.completed_at ||
          candidate?.canceled ||
          candidate?.deleted,
      },
      {
        icon: 'key',
        title: t('admin.candidate.loginAsClient'),
        onClick: loginAsClient,
        disabled:
          !hasPermission(AdminPermissions.LoginAsClient) ||
          company?.support_access_level === 'no_access' ||
          (company?.support_access_level === 'request' && !hasActiveRequestForCompany),
      },
      {
        icon: 'external-link',
        title: t('admin.candidate.openAdminCandidateLink'),
        onClick: () =>
          window.open(
            `${process.env.REACT_APP_WEB_URL}/form_submission/candidate/dashboard/${candidate.token}?admin=true`,
            '_blank',
          ),
        disabled: !candidate?.token,
      },
      {
        icon: 'copy',
        title: t('admin.candidate.copyCandidateLink'),
        onClick: () =>
          handleCopyToClipboard(
            `${process.env.REACT_APP_WEB_URL}/form_submission/candidate/dashboard/${candidate.token}`,
            'Candidate link copied to clipboard',
          ),
        disabled: !candidate?.token,
      },
      {
        icon: 'external-link',
        title: t('admin.candidate.openPartnerLink'),
        onClick: () => window.open(candidate.partner_link, '_blank'),
        disabled: !candidate?.partner_link || !hasPermission(AdminPermissions.LoginAsClient),
      },
      {
        icon: 'shield',
        title: 'Generate Portal OTP',
        onClick: () => setShowPortalOtpModal(true),
      },
      {
        icon: 'edit',
        title: t('common.addNote'),
        onClick: () => setAddNoteModal(true),
      },
      {
        icon: 'mail',
        title: t('common.sendEmail'),
        onClick: () => setAddNotificationModal(true),
      },
      {
        icon: 'trash',
        title: t('common.delete'),
        onClick: () => setDeleteConfirmation(true),
        hide: candidate?.deleted,
      },
      {
        icon: 'refresh-ccw',
        title: t('common.restore'),
        onClick: () => setRestoreConfirmation(true),
        hide: !candidate?.deleted,
      },
    ];
  }, [candidate, company, hasActiveRequestForCompany]);

  const UPLOAD_SECTIONS = [
    {
      id: 'candidateIds',
      title: 'admin.candidate.candidateIds',
      uploads: [selfieUpload, ...signatureUploads].filter(Boolean),
      idDocumentGroups,
      startOpen: true,
      headerActions: [
        {
          icon: 'camera',
          tip: t('admin.candidate.components.supportSelfieUpload.uploadSelfie'),
          onClick: () => setShowSupportSelfieUploadModal(true),
        },
        {
          icon: 'upload',
          tip: t('admin.candidate.components.documentUpload.uploadDocument', 'Upload document'),
          onClick: () => setShowDocumentUploadModal(true),
        },
      ],
      onClick: {
        delete: confirmDeleteUpload,
      },
      props: {
        allowDelete: true,
      },
    },
    {
      id: 'supportingDocuments',
      title: 'admin.candidate.supportingDocuments',
      uploads: supportUploads,
      startOpen: null,
      onClick: {
        delete: confirmDeleteUpload,
      },
      props: {
        allowDelete: true,
      },
    },
    {
      id: 'otherDocuments',
      title: 'admin.candidate.otherDocuments',
      uploads: otherDocumentsUploads,
      startOpen: null,
      onClick: {
        delete: confirmDeleteUpload,
      },
      props: {
        allowDelete: false,
      },
    },
    {
      id: 'resultUploads',
      title: 'common.results',
      uploads: resultUploads,
      startOpen: null,
      onClick: {
        delete: confirmDeleteUpload,
      },
      props: {
        allowDelete: true,
      },
    },
    {
      id: 'consentsDisclosures',
      title: 'admin.candidate.consentsAndDisclosures',
      uploads: consentsDisclosuresUploads,
      startOpen: null,
      onClick: {
        delete: confirmDeleteUpload,
      },
      props: {
        allowDelete: true,
      },
    },
    {
      id: 'rejectedUploads',
      title: 'admin.candidate.rejectedUploads',
      uploads: rejectedUploads,
      startOpen: false,
      props: {
        rejected: true,
        allowDelete: false,
      },
    },
  ];

  const overlays = (
    <>
      <Alert
        show={cancelConfirmation}
        title={t('admin.candidate.cancelCandidate.title', { title: candidate?.first_name })}
        ok="Yes"
        cancel="No"
        onOk={cancelCheck}
        onCancel={() => setCancelConfirmation(false)}
      />

      <Alert
        show={showReConsentConfirmation}
        title={t('admin.candidate.requestReConsentConfirm.title', { title: candidate?.first_name })}
        message={t('admin.candidate.requestReConsentConfirm.message')}
        ok={t('admin.candidate.requestReConsent')}
        cancel={t('common.cancel')}
        onOk={handleRequestReConsent}
        onCancel={() => setShowReConsentConfirmation(false)}
      />

      <Alert
        show={deleteConfirmation}
        title={t('admin.candidate.deleteCandidate.title', { title: candidate?.first_name })}
        ok="Delete"
        type="error"
        icon={Delete02Icon}
        showConfirmationInput
        onOk={deleteCandidate}
        cancel="Cancel"
        onCancel={() => setDeleteConfirmation(false)}
      />

      <Alert
        show={restoreConfirmation}
        title={t('admin.candidate.restoreCandidate.title', { title: candidate?.first_name })}
        ok="Restore"
        onOk={restoreCandidate}
        cancel="Cancel"
        onCancel={() => setRestoreConfirmation(false)}
      />

      <Alert
        show={showDeleteUploadAlert}
        title={t('admin.candidate.deleteUpload.title')}
        ok="Delete"
        type="error"
        icon={Delete02Icon}
        onOk={handleDeleteUpload}
        cancel="Cancel"
        onCancel={() => setShowDeleteUploadAlert(false)}
      />

      <RejectModal
        visible={rejectModal}
        checkValues={checkValues?.values}
        uploads={[selfieUpload, ...idUploads]}
        onClose={() => setRejectModal(false)}
        onReject={handleReject}
      />

      <IdVerificationModal
        idvSummary={idvSummary}
        visible={showIdVerificationModal}
        onClose={() => setShowIdVerificationModal(false)}
      />

      <KeyValueModal
        visible={showCompanyDetailsModal}
        onClose={() => setShowCompanyDetailsModal(false)}
        title={t('admin.candidate.companyDetails.title')}
        data={{
          [t('common.company')]: company?.name,
          [t('admin.candidate.companyDetails.supportAccessLevel')]: company?.support_access_level,
          [t('admin.candidate.companyDetails.contactEmail')]: company?.contact_email,
          [t('admin.candidate.companyDetails.accountType')]: company?.account_type,
          [t('admin.candidate.companyDetails.checkmateEntity')]:
            {
              checkmate_technology_limited: 'NZ',
              checkmate_technology_pty_ltd: 'AU',
              checkmate_inc: 'US',
            }[company?.checkmate_entity] || '-',
        }}
      />

      <AddNoteModal
        visible={addNoteModal}
        onSubmit={handleAddCandidateNote}
        onClose={() => setAddNoteModal(false)}
      />

      <PortalOtpModal
        visible={showPortalOtpModal}
        candidateEmail={candidate?.email}
        onGetOtp={() => getPortalOtp(candidateId).unwrap()}
        onGenerate={() => generatePortalOtp(candidateId).unwrap()}
        onClose={() => setShowPortalOtpModal(false)}
      />

      <AddNotificationModal
        visible={addNotificationModal}
        companyId={company?.id}
        modelParams={{
          candidate_id: candidateId,
        }}
        onSubmit={handleAddCandidateNotification}
        onClose={() => setAddNotificationModal(false)}
      />

      <CandidateUpdateModal
        candidate={candidate}
        visible={updateCandidateModal}
        onSubmit={updateCandidateDetails}
        onClose={() => setUpdateCandidateModal(false)}
      />

      <CandidateDocumentUploadModal
        candidate={candidate}
        company={company}
        visible={showDocumentUploadModal}
        onUpdate={refetchUploads}
        onClose={() => setShowDocumentUploadModal(false)}
        onAfterUpload={handleAfterUpload}
      />

      <SupportSelfieUploadModal
        candidate={candidate}
        company={company}
        existingSelfieIds={(uploadsData || [])
          .filter(upload => upload.metadata?.selfie)
          .map(upload => upload.id)}
        visible={showSupportSelfieUploadModal}
        onUpdate={handleSupportSelfieUploaded}
        onClose={() => setShowSupportSelfieUploadModal(false)}
      />
    </>
  );

  return (
    <AdminPage
      loading={loading || loadingChecks || isVerifyingId || isIdvLoading}
      title={
        <div className="d-flex align-items-center u-gap-small">
          {candidate?.name}
          {NonCustomerAccountTypes.includes(company?.account_type) && (
            <OverlayTrigger
              overlay={<Tooltip className="u-capitalize">{company.account_type}</Tooltip>}>
              <span>
                <HugeiconsIcon
                  icon={BucketIcon}
                  size="24"
                  strokeWidth="2"
                  className="text-warning"
                />
              </span>
            </OverlayTrigger>
          )}
        </div>
      }
      copyToClipboard={true}
      allowedRole={AdminPermissions.ProcessCandidateChecks}>
      {overlays}

      <div className="flex items-center gap-4 px-12 bg-neutral-1 pb-4 pt-8">
        <RowDetails.Status status={candidate?.status} />
        {candidate?.volunteer && (
          <div className="flex items-center gap-1 bg-pink text-neutral-1 rounded-full px-4 py-1 text-xl font-regular border border-pink-1 mr-8">
            <HugeiconsIcon icon={UserLove01Icon} size="14" strokeWidth="1.5" /> Volunteer
          </div>
        )}
      </div>
      <div className="flex items-start gap-4 bg-neutral-1 px-16 py-4 ml-2">
        <RowDetails className={cn(styles.details, 'flex-wrap gap-4')}>
          <RowDetails.Column title={t('common.id')} data={candidate?.id} />
          <RowDetails.Column title={t('common.email')} data={candidate?.email} />
          <RowDetails.Column title={t('common.company')} data={company?.name} />
          <RowDetails.Column title={t('common.BrandDisplayName')} data={brand?.brand_name} />
          <RowDetails.Column title={t('common.brand')} data={brand?.name || 'N/A'} />
          <RowDetails.Column title={t('common.department')} data={candidate?.department || 'N/A'} />
          <RowDetails.Column title={t('common.jobRole')} data={candidate?.job_role || 'N/A'} />
          <RowDetails.Column
            title={t('common.createdOn')}
            data={<Moment format={dateSlashFormat}>{candidate?.created_at}</Moment>}
          />
          <RowDetails.Column
            title={t('common.submittedOn')}
            data={<Moment format={dateSlashFormat}>{candidate?.submitted_at}</Moment>}
          />
          <RowDetails.Column
            title={t('common.completedOn')}
            data={
              candidate?.completed_at ? (
                <Moment format={dateSlashFormat}>{candidate?.completed_at}</Moment>
              ) : (
                '-'
              )
            }
          />
          <RowDetails.Column
            title={t('common.dataDeletedAt')}
            data={
              candidate?.data_deleted_at ? (
                <Moment format={dateSlashFormat}>{candidate?.data_deleted_at}</Moment>
              ) : (
                '-'
              )
            }
          />
        </RowDetails>
        <div className="flex items-center gap-4">
          <IconStatusButton
            icon="id-badge"
            prefix="fa"
            tooltip={t(idvStatusKey)}
            status={idvStatus}
            onClick={() => setShowIdVerificationModal(true)}
          />
          <ActionsMenu>
            {actions.map((action, index) =>
              action.hide ? null : (
                <ActionsMenu.Item
                  key={action.title + index}
                  icon={action.icon}
                  title={action.title}
                  onClick={action.onClick}
                  disabled={action.disabled}
                />
              ),
            )}
          </ActionsMenu>
        </div>
      </div>

      <Tabs defaultActiveKey="checks" className="tabs no-padding padding-small !pl-16" mountOnEnter>
        <Tab eventKey="checks" title="Candidate Checks">
          <div className={styles.section}>
            <div className={styles.checks}>
              {orderedChecks.map(check => {
                const checkType = checkTypes?.find(checkType => checkType.slug === check.type);

                return (
                  <CandidateCheckDetails
                    key={check.id}
                    candidate={candidate}
                    company={company}
                    referees={referees}
                    check={check}
                    checkType={checkType}
                    linkableChecks={orderedChecks.map(candidateCheck => ({
                      ...candidateCheck,
                      title:
                        checkTypes?.find(type => type.slug === candidateCheck.type)?.title ||
                        candidateCheck.type,
                    }))}
                    onActivityChange={refetchChecks}
                    companyChecks={companyChecks}
                    checkValues={checkValues}
                    setLoading={setLoading}
                    addOrUpdateCheck={addOrUpdateCheck}
                    onDocumentUpload={refetchUploads}
                    checkNotes={getCheckNotes(check?.id)}
                    refetchSupportNotes={refetchCheckNotes}
                    supportUploads={supportUploads}
                    otherDocumentsUploads={otherDocumentsUploads}
                    resultUploads={resultUploads}
                  />
                );
              })}
            </div>
          </div>

          <div className={cn(styles.section, 'd-flex', 'align-items-start')}>
            <CollapsibleBlock className="u-margin-right--large u-width-50" startOpen={true}>
              <CollapsibleBlock.Header>
                <h1 className={styles.title}>{t('admin.candidate.candidateValues')}</h1>
              </CollapsibleBlock.Header>
              <CollapsibleBlock.Content>
                <div className="u-margin-y">
                  <CandidateCheckValues
                    checkValues={checkValues}
                    updateCandidateChecksValues={handleUpdateCandidateChecksValues}
                  />
                </div>
              </CollapsibleBlock.Content>
            </CollapsibleBlock>

            <div className="u-width-50">
              {UPLOAD_SECTIONS.map(section => (
                <CollapsibleBlock
                  key={section.id}
                  className={section.id !== 'rejectedUploads' ? 'u-margin-bottom' : ''}
                  startOpen={
                    section.startOpen === null
                      ? (section.uploads && section.uploads.length > 0) ||
                        (section.idDocumentGroups && section.idDocumentGroups.length > 0)
                      : section.startOpen
                  }>
                  <CollapsibleBlock.Header>
                    <h1 className={styles.title}>{t(section.title)}</h1>
                    {(section.headerActions || []).map((action, index) => (
                      <IconButton
                        key={`${section.id}-header-action-${index}`}
                        icon={action.icon}
                        tip={action.tip}
                        onClick={action.onClick}
                      />
                    ))}
                  </CollapsibleBlock.Header>
                  <CollapsibleBlock.Content>
                    <div className="u-margin-y d-flex flex-wrap gap-default">
                      {/* ID document groups: one card per id_upload_detail (form shows front/back + previews) */}
                      {section.id === 'candidateIds' &&
                        (section.idDocumentGroups || []).map(group => {
                          const { frontUpload, idUploadDetail } = group;
                          const getStatusBoolean = status => status === IDV_SUB_STATUS.PASSED;
                          const canLinkToNameChangeDocument =
                            isNameChangeDocumentUploaded?.length > 0 &&
                            idUploadDetail?.selected_id_type &&
                            !NAME_CHANGE_DOCUMENTATION_ID_TYPES.includes(
                              idUploadDetail.selected_id_type,
                            );

                          return (
                            <IdVerificationForm
                              key={`id-group-${frontUpload.id}`}
                              upload={frontUpload}
                              idUploadDetail={idUploadDetail}
                              getStatusBoolean={getStatusBoolean}
                              onIdStatusUpdate={handleUpdateIDVerificationStatus}
                              onUpdateIdDetails={
                                section.id === 'candidateIds' ? handleUpdateIdDetails : undefined
                              }
                              onSupportRejectId={handleSupportRejectId}
                              onReinstateIdFromSupport={handleReinstateIdFromSupport}
                              sectionId={section.id}
                              sectionProps={section.props}
                              linkToNameChangeDocument={canLinkToNameChangeDocument}
                              nameChangeDocumentUploads={isNameChangeDocumentUploaded}
                              onDelete={
                                section.props.allowDelete
                                  ? () =>
                                      section.onClick.delete(
                                        [
                                          group.backUpload?.id,
                                          frontUpload.id,
                                          ...(group.previewUploads || []).map(u => u.id),
                                        ].filter(Boolean),
                                      )
                                  : undefined
                              }
                              uploadsMap={uploadsMap}
                              t={t}
                            />
                          );
                        })}
                      {(section.uploads || []).map(upload => {
                        const idUploadDetail = upload.id_upload_detail;
                        const getStatusBoolean = status => status === IDV_SUB_STATUS.PASSED;

                        const canLinkToNameChangeDocument =
                          isNameChangeDocumentUploaded?.length > 0 &&
                          upload.id_upload_detail?.selected_id_type &&
                          !NAME_CHANGE_DOCUMENTATION_ID_TYPES.includes(
                            upload.id_upload_detail.selected_id_type,
                          );

                        return (
                          <IdVerificationForm
                            key={upload.id}
                            upload={upload}
                            idUploadDetail={idUploadDetail}
                            getStatusBoolean={getStatusBoolean}
                            onIdStatusUpdate={handleUpdateIDVerificationStatus}
                            onUpdateIdDetails={
                              section.id === 'candidateIds' ? handleUpdateIdDetails : undefined
                            }
                            onSupportRejectId={handleSupportRejectId}
                            onReinstateIdFromSupport={handleReinstateIdFromSupport}
                            sectionId={section.id}
                            sectionProps={section.props}
                            linkToNameChangeDocument={canLinkToNameChangeDocument}
                            nameChangeDocumentUploads={isNameChangeDocumentUploaded}
                            onDelete={
                              section.props.allowDelete
                                ? () => section.onClick.delete(upload.id)
                                : undefined
                            }
                            uploadsMap={section.id === 'candidateIds' ? uploadsMap : undefined}
                            showAttachToReportToggle={section.id === 'supportingDocuments'}
                            onToggleAttachToReport={
                              section.id === 'supportingDocuments'
                                ? (() => {
                                    const uploadForRow = upload;
                                    return async (uploadId, attachToReport) => {
                                      await updateUpload({
                                        id: uploadId,
                                        metadata: {
                                          ...(uploadForRow?.metadata || {}),
                                          attach_to_report: attachToReport,
                                        },
                                      });
                                      refetchUploads();
                                    };
                                  })()
                                : undefined
                            }
                            t={t}
                          />
                        );
                      })}
                    </div>
                  </CollapsibleBlock.Content>
                </CollapsibleBlock>
              ))}
            </div>
          </div>
        </Tab>
        <Tab eventKey="history" title="History">
          <AdminCandidateHistoryTab
            candidate={candidate}
            company={company}
            companyChecks={companyChecks}
            referees={referees}
            historyData={historyData}
            fetchHistoryData={fetchHistoryData}
          />
        </Tab>
      </Tabs>
    </AdminPage>
  );
};

export default AdminCandidate;
